import React, { useState } from 'react';
import { System } from '../types';
import Modal from './Modal';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface DeleteSystemModalProps {
  isOpen: boolean;
  onClose: () => void;
  system: System | null;
  onRefresh: () => void;
}

const DeleteSystemModal: React.FC<DeleteSystemModalProps> = ({ 
  isOpen, 
  onClose, 
  system, 
  onRefresh 
}) => {
  const { apiUrl } = useDatabaseContext();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string>('');

  const handleDelete = async () => {
    if (!system) return;

    setDeleting(true);
    setError('');

    try {
      const response = await fetch(`${apiUrl}/systems/${system.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        onRefresh();
        onClose();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete system.');
      }
    } catch (err) {
      setError('An error occurred while deleting the system.');
    } finally {
      setDeleting(false);
    }
  };

  if (!system) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete System"
      size="md"
    >
      <div className="text-center">
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg
            className="h-6 w-6 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Are you sure you want to delete this system?
          </h3>
          <div className="text-sm text-slate-600 space-y-2">
            <p>
              <strong>System:</strong> {system.name}
              {system.systemId && (
                <span> (ID: {system.systemId})</span>
              )}
            </p>
            <p>
              <strong>Number of Lines:</strong> {system.numberOfLines}
            </p>
            {system.description && (
              <p>
                <strong>Description:</strong> {system.description}
              </p>
            )}
          </div>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            <strong>Warning:</strong> This action cannot be undone. The system configuration will be permanently removed.
          </div>
        </div>

        <div className="flex justify-center space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete System'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteSystemModal;